import type {AbstractGeometryData, CompleteGeometryData, ReifiedGeometryData} from "#template/interfaces/geometry-data"
import type {AbstractMeshData, CompleteMeshData, ReifiedMeshData} from "#template/interfaces/mesh-data"
import type {ValueTypeDescriptor} from "#template/runtime-graph/descriptors"
import {inletDescriptor, outletDescriptor, scopeDescriptor} from "#template/runtime-graph/descriptors"
import type {ObjectPath as _ObjectPath} from "#template/interfaces/scene-object"
import type {ObjectPathPart} from "#template/interactions"
import type {TemplateContext} from "#template/compile-context"
import type {ImageGenerator, IMaterialData, InputMaterialConnection, MaterialGraph} from "@cm/material-nodes"
import {keyForMaterialData} from "@cm/material-nodes"
import type {Matrix4 as _Matrix4} from "@cm/math"
import type {DataObjectWithRelated} from "@cm/utils"
import {deepCopy, deepEqual} from "@cm/utils"

export type HashedMaterialNodeWithColorOutput = {
    node: InputMaterialConnection<"color">
    contentHash: string
}

export namespace TypeDescriptors {
    export type Desc<T> = ValueTypeDescriptor<T> | undefined

    export const inlet = inletDescriptor
    export const outlet = outletDescriptor
    export const builder = scopeDescriptor

    export function Primitive<T>(): Desc<T> {
        return undefined
    }

    export function Tuple<T extends unknown[]>(): Desc<T> {
        return {deepCompare: 1}
    }

    export const Number = Primitive<number>()
    export const Boolean = Primitive<boolean>()
    export const String = Primitive<string>()

    export function Function<T>(): Desc<T> {
        return undefined
    }

    export function StaticFunction<T>(): Desc<T> {
        return {
            deepCompare: (a, b) => {
                return typeof a === typeof b
            },
            deepCopy: (a) => a,
        }
    }

    export function Identity<T>(key?: keyof T): Desc<T> {
        if (key) {
            return {
                deepCompare: (a, b) => {
                    if (!a || !b) return a === b
                    return a[key] === b[key]
                },
                deepCopy: (a) => a,
            }
        } else {
            return undefined
        }
    }

    export function Array<T>(elemType: Desc<T>): Desc<T[]> {
        if (!elemType)
            return {
                deepCompare: 1,
                deepCopy: (a) => [...a],
            }
        const {deepCompare: _deepCompare, deepCopy: _deepCopy} = elemType

        return {
            deepCompare: _deepCompare
                ? (a, b) => {
                      if (!a || !b) return a === b
                      if (a.length !== b.length) return false
                      for (let i = 0; i < a.length; i++) {
                          if (typeof _deepCompare === "function") {
                              if (!_deepCompare(a[i], b[i])) return false
                          } else if (!deepEqual(a[i], b[i], _deepCompare === true ? undefined : _deepCompare)) return false
                      }
                      return true
                  }
                : 1,
            deepCopy: _deepCopy
                ? (a: T[]) => {
                      if (typeof _deepCopy === "function") return a.map(_deepCopy)
                      return a.map((x) => deepCopy(x, _deepCopy === true ? undefined : _deepCopy))
                  }
                : (a) => [...a],
        }
    }

    export function Nullable<T>(x: Desc<T>): Desc<T | null> {
        if (!x) return undefined
        const {deepCompare: _deepCompare, deepCopy: _deepCopy} = x

        return {
            deepCompare: _deepCompare
                ? (a, b) => {
                      if (typeof _deepCompare === "function") return _deepCompare(a, b)
                      return deepEqual(a, b, _deepCompare === true ? undefined : _deepCompare)
                  }
                : undefined,
            deepCopy: _deepCopy
                ? (a) => {
                      if (a === null) return null
                      if (typeof _deepCopy === "function") return _deepCopy(a)
                      return a
                  }
                : undefined,
        }
    }

    export function JSON<T = unknown>(): Desc<T> {
        return {deepCompare: true}
    }

    export function ShallowJSON<T = unknown>(): Desc<T> {
        return {deepCompare: 1}
    }

    export function Map<K, V>(valueType: Desc<V>): Desc<Map<K, V>> {
        const _deepCompare = valueType?.deepCompare

        return {
            deepCompare: (a, b) => {
                if (!a || !b) return a === b
                if (a.size !== b.size) return false
                for (const [key, av] of a) {
                    if (!b.has(key)) return false
                    const bv = b.get(key) as V
                    if (_deepCompare) {
                        if (typeof _deepCompare === "function") {
                            if (!_deepCompare(av, bv)) return false
                        } else if (!deepEqual(av, bv, _deepCompare === true ? undefined : _deepCompare)) return false
                    } else if (av !== bv) return false
                }
                return true
            },
            deepCopy: (a) => new globalThis.Map(a),
        }
    }

    export function Set<V>(): Desc<Set<V>> {
        return undefined
        //TODO: map deep compare
    }

    export const Matrix4: Desc<_Matrix4> = {
        deepCompare: (a, b) => {
            if (!a || !b) return a === b
            return a.equals(b)
        },
        deepCopy: (a) => a.copy(),
    }

    export const MaterialData: Desc<IMaterialData> = {
        deepCompare: (a, b) => {
            if (!a || !b) return a === b
            return keyForMaterialData(a) === keyForMaterialData(b)
        },
    }
    export const MaterialGraph: Desc<MaterialGraph> = {
        deepCompare: (a, b) => {
            if (!a || !b) return a === b
            return a.uniqueId === b.uniqueId
        },
        deepCopy: (a) => a.clone({cloneSubNode: () => true}),
    }
    export const MaterialNodeWithColorOutput: Desc<HashedMaterialNodeWithColorOutput> = {
        deepCompare: (a, b) => {
            if (!a || !b) return a === b
            return a.contentHash === b.contentHash
        },
        deepCopy: (a: HashedMaterialNodeWithColorOutput) => ({
            node: a.node.clone({cloneSubNode: () => true}),
            contentHash: a.contentHash,
        }),
    }

    export const DataObject = Identity<DataObjectWithRelated>("legacyId")

    export const AbstractMeshData = Identity<AbstractMeshData>("contentHash")
    export const ReifiedMeshData = Identity<ReifiedMeshData>("contentHash")

    export const CompleteMeshData: Desc<CompleteMeshData> = {
        deepCompare: (a, b) => {
            if (!a || !b) return a === b
            return a.reified.contentHash === b.reified.contentHash && a.abstract.contentHash === b.abstract.contentHash
        },
        deepCopy: (a) => a,
    }

    export const AbstractGeometryData = Identity<AbstractGeometryData>("contentHash")
    export const ReifiedGeometryData = Identity<ReifiedGeometryData>("contentHash")

    export const CompleteGeometryData: Desc<CompleteGeometryData> = {
        deepCompare: (a, b) => {
            if (!a || !b) return a === b
            return a.reified.contentHash === b.reified.contentHash && a.abstract.contentHash === b.abstract.contentHash
        },
        deepCopy: (a) => a,
    }

    export const ImageGenerator: Desc<ImageGenerator> = {
        deepCompare: (a, b) => {
            if (!a || !b) return a === b
            return a.imageNode.hash === b.imageNode.hash
        },
        deepCopy: (a) => a,
    }

    export const ObjectPath: Desc<_ObjectPath> = {
        deepCompare: (a, b) => {
            if (!a || !b) return a === b
            if (a.length !== b.length) return false
            for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
            return true
        },
        deepCopy: (a) => a,
    }

    export const ObjectPathPart: Desc<ObjectPathPart> = {
        deepCompare: (a, b) => {
            if (!a || !b) return a === b
            if (a.part !== b.part) return false
            if (a.path.length !== b.path.length) return false
            for (let i = 0; i < a.path.length; i++) if (a.path[i] !== b.path[i]) return false
            return true
        },
        deepCopy: (a) => a,
    }

    export const TemplateContext: Desc<TemplateContext> = {
        deepCompare: (a, b) => {
            if (!a || !b) return a === b
            else if (a.sceneManager !== b.sceneManager) return false
            else if (a.templateMatrix.equals(b.templateMatrix) === false) return false
            else if (a.lodType !== b.lodType) return false
            else if (a.computeDummyReifiedMeshData !== b.computeDummyReifiedMeshData) return false
            else if (a.computeDataObjectReferences !== b.computeDataObjectReferences) return false
            else if (a.defaultCustomerId !== b.defaultCustomerId) return false
            else if (a.showDimensions !== b.showDimensions) return false
            else if (a.instanceIdChain.length !== b.instanceIdChain.length || a.instanceIdChain.some((id, idx) => id !== b.instanceIdChain[idx])) return false
            else if (a.localIds !== b.localIds) return false
            return true
        },
        deepCopy: (a) => ({...a}),
    }
}
